<!-- Ostukorvi vaheleht -->
<template>
  <div class="page-properties">
    <page-header>
      <h1>{{t('ostukorv')}}</h1>
      <v-dialog v-model="empty_cart" width="auto" transition="none">
        <v-card>
          <v-toolbar color="white" :title="t('kinnitus')">
            <v-btn icon="mdi-close" @click="empty_cart = false"></v-btn>
          </v-toolbar>
          <v-card-text>
            <div class="text-h5 justify-center text-center">{{t('eemalda_conf')}}</div>
          </v-card-text>
          <v-divider></v-divider>
          <div class="pa-4">
            <v-btn class="text-none mr-4 font-weight-bold" color="eraklient" min-width="92" rounded @click.prevent="deleteCart" @click="empty_cart = false; showToastSuccess(t('tuhjendatud'))" v-bind="props">{{t('jah')}}</v-btn>
            <v-btn class="text-none font-weight-bold" min-width="92" rounded @click="empty_cart = false">{{t('ei')}}</v-btn>
          </div>
        </v-card>
      </v-dialog>
      <div class="pa-4">
        <v-btn class="mb-2" v-if="Object.keys(cart).length > 0" :title="t('tühjenda_korv')" icon="mdi-delete" @click="empty_cart = true" aria-haspopup="true" :aria-expanded="valid"></v-btn>
      </div>
    </page-header>

    <v-sheet v-if="Object.keys(cart).length > 0" class="cart-body">
      <v-sheet class="w-100 cart-panel rounded-lg" elevation="5">
        <v-list class="pa-6">
          <template v-for="(item, productId, index) in cart" :key="productId">
            <v-list-item class="pa-0">
              <div class="d-flex w-100 my-6 align-center cart-content">
                <div class="cart-main-info d-flex flex-column flex-1-1 mr-6">
                  <a class="d-flex align-center" :href="'/tooted/' + getLinkCat(item.product) + '/' + item.product.id" draggable="false">
                    <div class="d-flex align-center justify-center mr-5 cart-img">
                      <v-img v-if="getCartImage(item.product)" width="80" height="80" cover :src="getCartImage(item.product)" draggable="false"></v-img>
                      <img class="cart-icon" v-else src="../../assets/images/lohnakas.webp" alt="Lohnakas logo" draggable="false"/>
                    </div>
                    <div class="d-flex flex-column w-100">
                      <span class="font-weight-bold text-left"> {{ getHeader(item.product) }} </span>
                    </div>
                  </a>
                </div>
                <div class="cart-item-controls d-flex flex-column">
                  <div class="flex-column d-flex text-center">
                    <div class="product-price font-weight-bold">
                      <div class="price-pill">
                        <span v-if="getOriginalLineTotal(item) !== null" class="old-price">
                          {{ formatPrice(getOriginalLineTotal(item)) }}
                        </span>
                        <span class="price-value">{{ formatPrice(getLineTotal(item)) }}</span>
                        <span class="price-currency">EUR</span>
                      </div>
                    </div>
                  </div>
                  <div class="cart-quantity-control quantity-picker">
                    <v-btn aria-label="Decrease Quantity" color="eraklient" @click="decreaseQuantity(productId)" icon="mdi-minus" size="30px"><v-icon size="20px"></v-icon></v-btn>
                    <div class="quantity-input-wrap">
                      <input class="quantity-input" :title="$t('valige_kogus')" role="textbox" :aria-label="$t('quantity_input')" v-model="item.quantity">
                    </div>
                    <v-btn aria-label="Increase Quantity" color="eraklient" @click="increaseQuantity(productId)" icon="mdi-plus" size="30px"><v-icon size="20px"></v-icon></v-btn>
                  </div>
                </div>
                <div v-if="hasSprayRollOnOption(item.product) || hasBottleSizeOption(item.product)" class="cart-config-grid">
                  <div v-if="hasSprayRollOnOption(item.product)" class="config-card cart-config-card">
                    <div class="config-label">{{ $t('lohna_tuup') }}</div>
                    <div class="option-toggle" role="radiogroup" :aria-label="$t('lohna_tuup')">
                      <button type="button" :class="['option-pill', 'option-pill--cart', { 'option-pill--active': (item.choice || 'Spray') === 'Spray' }]" :aria-pressed="(item.choice || 'Spray') === 'Spray'" @click.stop.prevent="updateChoice(productId, 'Spray')">
                        Spray
                      </button>
                      <button type="button" :class="['option-pill', 'option-pill--cart', { 'option-pill--active': (item.choice || 'Spray') === 'Roll-on' }]" :aria-pressed="(item.choice || 'Spray') === 'Roll-on'" @click.stop.prevent="updateChoice(productId, 'Roll-on')">
                        Roll-on
                      </button>
                    </div>
                  </div>
                  <div v-if="hasBottleSizeOption(item.product)" class="config-card cart-config-card">
                    <div class="config-label">{{ $t('vali_pudeli_suurus') }}</div>
                    <div class="option-toggle" role="radiogroup" :aria-label="$t('vali_pudeli_suurus')">
                      <button
                        v-for="bs in getBottleSizes(item.product)"
                        :key="bs.size"
                        type="button"
                        :class="['option-pill', 'option-pill--cart', { 'option-pill--active': (item.size || getDefaultSizeFor(item.product)) === bs.size }]"
                        :aria-pressed="(item.size || getDefaultSizeFor(item.product)) === bs.size"
                        @click.stop.prevent="updateSize(productId, bs.size)"
                      >{{ bs.size }}</button>
                    </div>
                  </div>
                </div>
              </div>
            </v-list-item>
            <v-divider v-if="index < Object.keys(cart).length - 1" class="mx-3"></v-divider>
          </template>
        </v-list>
      </v-sheet>
      <v-sheet class="rounded-lg checkout text-center pa-4" elevation="5">
        <h2 class="text-left">{{ t('tellimus') }}</h2>
        <v-list role="listbox">
          <template v-for="(item, productId, index) in cart" :key="`order-${productId}`">
            <v-list-item class="pa-0">
              <div class="order-items">
                <div class="order-item-details">
                  <div class="order-item-title">{{ item.quantity }}x {{ getHeader(item.product) }}</div>
                  <div v-if="hasSprayRollOnOption(item.product) || hasBottleSizeOption(item.product)" class="order-item-meta">
                    <span v-if="hasSprayRollOnOption(item.product)">{{ item.choice || 'Spray' }}</span>
                    <span v-if="hasSprayRollOnOption(item.product) && hasBottleSizeOption(item.product)" class="order-item-dot">·</span>
                    <span v-if="hasBottleSizeOption(item.product)">{{ item.size || getDefaultSizeFor(item.product) }}</span>
                  </div>
                </div>
                <div class="price-pill price-pill--summary">
                  <span class="price-value">{{ formatPrice(getLineTotal(item)) }}</span>
                  <span class="price-currency">EUR</span>
                </div>
              </div>
            </v-list-item>
            <v-divider v-if="index < Object.keys(cart).length - 1" class="order-divider"></v-divider>
          </template>
          <v-divider class="order-divider"></v-divider>
        </v-list>
        <div class="order-items pt-5">
          <div class="text-uppercase">{{t('kokku')}}</div>
          <div class="font-weight-bold order-total">
            <span class="order-total-value">{{ formatPrice($store.getters.cartTotal) }}</span>
            <span class="order-total-currency">EUR</span>
          </div>
        </div>
        <form class="mt-5" @submit.prevent="submit">
          <v-text-field
              variant="outlined"
              v-model="name.value.value"
              :error-messages="name.errorMessage.value"
              :label="t('nimi')"
          ></v-text-field>
          <v-text-field
              variant="outlined"
              v-model="phone.value.value"
              :error-messages="phone.errorMessage.value"
              :label="t('tel')"
          ></v-text-field>
          <v-text-field
              variant="outlined"
              v-model="email.value.value"
              :error-messages="email.errorMessage.value"
              :label="t('email')"
          ></v-text-field>
          <v-select
              variant="outlined"
              menu-icon="mdi-chevron-down"
              v-model="select_delivery.value.value"
              :items="topics"
              :error-messages="select_delivery.errorMessage.value"
              :label="t('kattesaamine')"
          ></v-select>
          <v-select
              variant="outlined"
              menu-icon="mdi-chevron-down"
              v-model="select_payment.value.value"
              :items="topics_payment"
              :error-messages="select_payment.errorMessage.value"
              :label="t('tasumine')"
          ></v-select>
          <v-textarea
              variant="outlined"
              v-model="message.value.value"
              :label="t('sonum_valik')"
          ></v-textarea>
          <input type="text" name="honeypot" id="honeypot" style="display: none;" v-model="honeypot.value.value">
          <v-btn color="eraklient" class="d-flex w-100" type="submit">{{t('saada')}}</v-btn>
        </form>
      </v-sheet>
    </v-sheet>

    <v-dialog v-model="orderPlaced">
      <v-sheet class="pa-4 text-center mx-auto" elevation="12" max-width="600" rounded="lg" width="100%">
        <v-icon class="mb-5" color="success" icon="mdi-check-circle" size="112"></v-icon>
        <h2 class="text-h5 mb-6">{{t('order_title')}}</h2>
        <p class="mb-4 text-medium-emphasis text-body-2">{{t('order_text')}}</p>
        <v-divider class="mb-4"></v-divider>
        <div class="text-end">
          <v-btn class="text-none" @click="orderPlaced = false" color="success" variant="flat" width="90" rounded>OK</v-btn>
        </div>
      </v-sheet>
    </v-dialog>

    <div class="empty-cart" v-if="Object.keys(cart).length === 0">
      <p>{{t('ostukorv_tuhi')}}</p>
      <v-btn color="eraklient" prepend-icon="mdi-chevron-left" to="/tooted" :text="t('tagasi_poodi')"></v-btn>
    </div>
  </div>
</template>

<script>
import {computed, ref, watchEffect} from 'vue';
import PageHeader from "@/components/PageHeader";
import {toast} from "vue3-toastify";
import {useField, useForm} from "vee-validate";
import { useStore } from 'vuex'
import {useI18n} from "vue-i18n";
import { formatPriceValue, getDefaultBottleSize, getProductUnitPrice, hasBottleSizePriceRange as productHasBottleSizePriceRange, isBottleSizeOptionEnabled, isSprayRollOnOptionEnabled, parseBottleSizes } from "@/utils/productOptions.js";

export default {
  name: "Cart",
  components: {PageHeader},
  data() {
    return {
      valid: false,
      empty_cart: false
    };
  },
  setup() {
    const { t } = useI18n()
    const store = useStore();
    const { handleSubmit, handleReset } = useForm({
      validationSchema: {
        name (value) {
          if (value?.length > 1) return true
          return t('sisesta_nimi')
        },
        phone (value) {
          if (value?.length > 6 && /[0-9-]+/.test(value)) return true
          return t('sisesta_tel')
        },
        email (value) {
          if (/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(value)) return true
          return t('sisesta_email')
        },
        select_delivery (value) {
          if (value) return true
          return t('kattesaamise_viis')
        },
        select_payment (value) {
          if (value) return true
          return t('tasumise_viis')
        },
      },
    })
    const name = useField('name')
    const phone = useField('phone')
    const email = useField('email')
    const select_delivery = useField('select_delivery')
    const select_payment = useField('select_payment')
    const message = useField('message')
    const honeypot = useField('honeypot')

    const topics = computed(() => [
      t('pakiautomaat'),
      t('ise_jargi'),
      t('Muu')
    ])

    const topics_payment = computed(() => [
      t('sularaha'),
      t('arve'),
    ])

    const deleteCart = () => {
      store.commit('deleteCart');
    };

    const orderPlaced = ref(false);

    const placeOrder = () => {
      orderPlaced.value = true;
    };

    const submit = handleSubmit(async values => {
      if (honeypot.value.value) {
        toast.success(t('mess_succ'));
        handleReset();
        return
      }
      try {
        const newValues = {
          ...values,
          subject: "E-poe tellimus",
          total: (store.getters.cartTotal).toFixed(2),
          cart: Object.keys(store.state.cart).map(productId => {
            const product = store.state.cart[productId].product;
            const quantity = store.state.cart[productId].quantity;
            return {
              header: product.header,
              price: product.price,
              new_price: product.new_price,
              choice: isSprayRollOnOptionEnabled(product) ? (store.state.cart[productId].choice || 'Spray') : null,
              has_spray_roll_on_option: isSprayRollOnOptionEnabled(product),
              size: isBottleSizeOptionEnabled(product) ? (store.state.cart[productId].size || getDefaultBottleSize(product)) : null,
              has_bottle_size_option: isBottleSizeOptionEnabled(product),
              quantity
            }
          })
        };
        const response = await fetch(`${process.env.VUE_APP_SERVER_URL}/send-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newValues)
        });

        const data = await response.text();
        console.log(data);
        if (response.ok) {
          window.scrollTo(0, 0);
          deleteCart();
          placeOrder();
          handleReset();
        }
        else {
          toast.error(t('order_err'));
        }
      }
      catch (error) {
        console.error('There has been a problem with your fetch operation:', error);
      }
    });

    return {
      name,
      phone,
      email,
      select_delivery,
      select_payment,
      topics,
      topics_payment,
      message,
      submit,
      orderPlaced,
      deleteCart,
      t,
      honeypot
    }
  },
  computed: {
    cartItemCount() {
      return this.$store.getters.cartItemCount;
    },
    cart() {
      return this.$store.state.cart;
    }
  },
  methods: {
    increaseQuantity(productId) {
      this.$store.commit('increaseQuantity', productId);
    },
    decreaseQuantity(productId) {
      this.$store.commit('decreaseQuantity', productId);
    },
    updateChoice(productId, choice) {
      this.$store.commit('updateCartChoice', { productId, choice });
    },
    updateSize(productId, size) {
      this.$store.commit('updateCartSize', { productId, size });
    },
    hasSprayRollOnOption(content) {
      return isSprayRollOnOptionEnabled(content);
    },
    hasBottleSizeOption(content) {
      return isBottleSizeOptionEnabled(content);
    },
    getBottleSizes(content) {
      return parseBottleSizes(content);
    },
    getDefaultSizeFor(content) {
      return getDefaultBottleSize(content);
    },
    hasBottleSizePriceRange(content) {
      return productHasBottleSizePriceRange(content);
    },
    getUnitPrice(content, size) {
      return getProductUnitPrice(content, size);
    },
    getLineTotal(item) {
      return this.getUnitPrice(item.product, item.size) * Number(item.quantity || 0);
    },
    getOriginalLineTotal(item) {
      if (this.hasBottleSizePriceRange(item.product) || !item.product?.new_price) {
        return null;
      }

      return Number(item.product.price) * Number(item.quantity || 0);
    },
    formatPrice(value) {
      return formatPriceValue(value);
    },
    showToastSuccess(value) {
      toast.success(value, {
        autoClose: 3000
      })
    },
    getHeader(content) {
      return (this.$i18n.locale === 'en' && content.header_en !== null) ? content.header_en : (this.$i18n.locale === 'ru' && content.header_ru !== null) ? content.header_ru : content.header;
    },
    getCartImage(content) {
      const image = content?.image;

      if (!image) return null;

      if (Array.isArray(image)) {
        return image[0] || null;
      }

      if (typeof image === 'string') {
        const trimmed = image.trim();

        if (!trimmed) return null;

        if (trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            return Array.isArray(parsed) ? parsed[0] || null : null;
          } catch (err) {
            console.log(err);
          }
        }

        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
          const values = trimmed
              .slice(1, -1)
              .split(',')
              .map((value) => value.replace(/^"|"$/g, '').trim())
              .filter(Boolean);
          return values[0] || null;
        }

        return trimmed;
      }

      return null;
    },
    getLinkCat(content) {
      return this.$store.getters.getLinkCat(content);
    }
  },
  mounted() {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      this.$store.commit('loadCart', JSON.parse(savedCart));
    }
    this.cartItemsWatchers = Object.keys(this.cart).map(productId =>
        watchEffect(() => {
          if (this.cart[productId]) {
            const quantity = this.cart[productId].quantity;
            this.$store.commit('updateQuantity', { productId, quantity });
          }
        })
    )
  },
  beforeRouteLeave(to, from, next) {
    this.orderPlaced = false;
    next();
  }
}
</script>

<style scoped>
img {
  width: 80px;
  height: 80px;
  object-fit: scale-down;
}

.v-card-title {
  font-size: 0.8em;
}

a:hover {
  color: #3b71f7;
}

.cart-body {
  display: flex;
  gap: 0 20px;
  align-items: flex-start;
}

.cart-panel {
  background: #ffffff;
  align-self: flex-start;
}

.cart-main-info {
  grid-area: info;
  min-width: 0;
  margin-right: 0 !important;
}

.cart-config-grid {
  grid-area: options;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
}

.cart-content {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    "info controls"
    "options controls";
  column-gap: 16px;
  row-gap: 10px;
  align-items: start;
}

.order-items {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  gap: 12px;
}

.order-item-details {
  min-width: 0;
  display: grid;
  gap: 3px;
  text-align: left;
}

.order-item-title {
  font-weight: 600;
  line-height: 1.35;
}

.order-item-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 0.82rem;
  color: #5d6b7a;
}

.order-item-dot {
  opacity: 0.72;
}

.order-divider {
  margin: 8px 12px;
}

.product-price {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.cart-item-controls {
  grid-area: controls;
  gap: 10px;
  align-items: flex-end;
  min-width: 132px;
}

.config-card {
  display: grid;
  gap: 8px;
}

.cart-config-card {
  min-width: 0;
}

.config-label {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5d6b7a;
}

.cart-quantity-control {
  width: 132px;
  justify-content: space-between;
}

.quantity-picker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.quantity-input-wrap {
  width: 48px;
  min-width: 48px;
  max-width: 48px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.05);
  flex: 0 0 48px;
}

.quantity-input {
  width: 100%;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1;
  color: #111827;
  outline: none;
}

.option-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-pill {
  min-width: 92px;
  min-height: 38px;
  padding: 8px 14px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 999px;
  background: #ffffff;
  color: #1f2d3d;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.05);
  transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease, color 160ms ease;
}

.option-pill--cart {
  min-width: 84px;
  min-height: 34px;
  padding: 6px 12px;
  font-size: 0.82rem;
}

.option-pill:hover {
  transform: translateY(-1px);
  border-color: rgba(59, 113, 247, 0.35);
}

.option-pill--active {
  background: linear-gradient(135deg, #3b71f7 0%, #5b8cff 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(59, 113, 247, 0.2);
}

.price-pill {
  width: auto;
  min-width: 132px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, #f6fbff 0%, #e3efff 100%);
  border: 1px solid rgba(59, 113, 247, 0.18);
  box-shadow: 0 10px 24px rgba(59, 113, 247, 0.12);
  color: #13315c;
}

.price-pill--summary {
  box-sizing: border-box;
  width: 132px;
  min-width: 132px;
  max-width: 132px;
  flex: 0 0 132px;
  padding: 6px 12px;
  box-shadow: none;
}

.price-pill--total {
  padding-inline: 16px;
}

.order-total {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  color: #13315c;
}

.order-total-value {
  font-size: 1.2rem;
  font-weight: 800;
  line-height: 1;
}

.order-total-currency {
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.price-value {
  font-size: 1rem;
  font-weight: 800;
  line-height: 1;
}

.price-pill:has(.old-price) .price-value {
  color: #e53935;
}

.price-currency {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(19, 49, 92, 0.08);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.old-price {
  color: rgba(17, 24, 39, 0.55);
  font-size: 0.8rem;
  text-decoration: line-through;
}

.checkout {
  min-width: 400px;
  height: 100%;
}

.empty-cart {
  text-align: left;
}

@media (max-width: 400px) {
  .cart-img {
    display: none !important;
  }

  .cart-config-grid {
    gap: 12px;
  }

  .cart-quantity-control {
    gap: 8px;
  }
}

@media (max-width: 1000px) {
  .cart-body {
    flex-direction: column;
  }
  .cart-content {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "info controls"
      "options options";
    column-gap: 12px;
    row-gap: 10px;
  }
  .checkout {
    min-width: unset;
    width: 100%;
  }
  .product-price {
    align-items: flex-start;
  }
  .cart-item-controls {
    align-items: flex-start;
  }
  .empty-cart {
    text-align: center;
  }
  .elevation-5 {
    box-shadow: none !important;
  }
  .pa-5 {
    padding-bottom: 20px !important;
  }
  .cart-config-grid {
    flex-direction: column;
    gap: 12px;
    margin-top: 2px;
    justify-self: stretch;
    align-items: stretch;
    width: 100%;
  }
  .cart-config-card {
    justify-items: start;
  }
  .config-label {
    text-align: left;
  }
  .option-toggle {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
    justify-content: flex-start;
  }
  .option-pill--cart {
    width: 100%;
    min-width: 0;
  }
}
</style>